/**
 * Markdown format toolbar for the web editor.
 *
 * Ported from `Sources/JNote/Editor/MarkdownFormatToolbar.swift` — group
 * order, separators, icons, tooltips and the font-size control all mirror
 * the macOS toolbar one-to-one. Media insertion (image/audio/video/attach)
 * is out of scope here; the web editor keeps its unified Attach button.
 */
import {
  ALargeSmall,
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Check,
  Code,
  Heading2,
  Italic,
  Link,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  Plus,
  Quote,
  SquareCode,
  Strikethrough,
  Table,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { FormatAction } from '../lib/editor/markdownFormatter.js';
import { EDITOR_FONT_SIZES, nextEditorFontSize } from '../lib/editor/fontSize.js';

interface MarkdownFormatToolbarProps {
  fontSize: number;
  onFontSizeChange: (size: number) => void;
  /** Mirrors the macOS `enabled` flag (dim + no hit testing when false). */
  enabled: boolean;
  onAction: (action: FormatAction) => void;
  /** Opens the Insert Link dialog (prefilled from the selection). */
  onInsertLink: () => void;
}

const ICON = { size: 14, strokeWidth: 1.75 } as const;

/** Matches the Swift displaySize: integers plain, fractional with one decimal. */
function displaySize(size: number): string {
  return size === Math.floor(size) ? String(size) : size.toFixed(1);
}

export function MarkdownFormatToolbar({
  fontSize,
  onFontSizeChange,
  enabled,
  onAction,
  onInsertLink,
}: MarkdownFormatToolbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Closing on outside pointer-down keeps parity with a native menu that
  // dismisses the moment the user clicks elsewhere.
  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: PointerEvent): void {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [menuOpen]);

  function stepSize(direction: 1 | -1): void {
    const next = nextEditorFontSize(fontSize, direction);
    if (next !== null) onFontSizeChange(next);
  }

  return (
    <div
      className={enabled ? 'format-toolbar' : 'format-toolbar format-toolbar-disabled'}
      role="toolbar"
      aria-label="Markdown formatting"
    >
      <div className="format-scroll">
        <div className="format-group">
          <button
            type="button"
            className="format-tool"
            title="Decrease font size"
            disabled={!enabled}
            onClick={() => stepSize(-1)}
          >
            <Minus {...ICON} />
          </button>
          <div className="format-size-wrap" ref={menuRef}>
            <button
              type="button"
              className="format-size-trigger"
              title="Editor font size"
              disabled={!enabled}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <ALargeSmall size={13} strokeWidth={1.75} />
              <span className="format-size-value">{displaySize(fontSize)}</span>
            </button>
            {menuOpen && (
              <div className="format-size-menu" role="menu" aria-label="Editor font size">
                {EDITOR_FONT_SIZES.map((size) => (
                  <button
                    type="button"
                    key={size}
                    role="menuitemradio"
                    aria-checked={Math.abs(size - fontSize) < 0.01}
                    className="format-size-option"
                    onClick={() => {
                      setMenuOpen(false);
                      onFontSizeChange(size);
                    }}
                  >
                    {displaySize(size)}
                    {Math.abs(size - fontSize) < 0.01 && (
                      <span className="format-size-check">
                        <Check size={12} strokeWidth={2} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="format-tool"
            title="Increase font size"
            disabled={!enabled}
            onClick={() => stepSize(1)}
          >
            <Plus {...ICON} />
          </button>
        </div>

        <div className="format-separator" />

        <div className="format-group">
          <button
            type="button"
            className="format-tool"
            title="Bold"
            disabled={!enabled}
            onClick={() => onAction({ type: 'bold' })}
          >
            <Bold {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Italic"
            disabled={!enabled}
            onClick={() => onAction({ type: 'italic' })}
          >
            <Italic {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Strikethrough"
            disabled={!enabled}
            onClick={() => onAction({ type: 'strikethrough' })}
          >
            <Strikethrough {...ICON} />
          </button>
        </div>

        <div className="format-separator" />

        <div className="format-group">
          <button
            type="button"
            className="format-tool"
            title="Heading"
            disabled={!enabled}
            onClick={() => onAction({ type: 'heading', level: 2 })}
          >
            <Heading2 {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Quote"
            disabled={!enabled}
            onClick={() => onAction({ type: 'quote' })}
          >
            <Quote {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Inline code"
            disabled={!enabled}
            onClick={() => onAction({ type: 'inlineCode' })}
          >
            <Code {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Code block"
            disabled={!enabled}
            onClick={() => onAction({ type: 'codeBlock' })}
          >
            <SquareCode {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Insert table"
            disabled={!enabled}
            onClick={() => onAction({ type: 'table' })}
          >
            <Table {...ICON} />
          </button>
        </div>

        <div className="format-separator" />

        <div className="format-group">
          <button
            type="button"
            className="format-tool"
            title="Bullet list"
            disabled={!enabled}
            onClick={() => onAction({ type: 'bulletList' })}
          >
            <List {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Numbered list"
            disabled={!enabled}
            onClick={() => onAction({ type: 'numberedList' })}
          >
            <ListOrdered {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Task list"
            disabled={!enabled}
            onClick={() => onAction({ type: 'taskList' })}
          >
            <ListTodo {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Horizontal rule"
            disabled={!enabled}
            onClick={() => onAction({ type: 'horizontalRule' })}
          >
            <Minus {...ICON} />
          </button>
        </div>

        <div className="format-separator" />

        <div className="format-group">
          <button
            type="button"
            className="format-tool"
            title="Align left"
            disabled={!enabled}
            onClick={() => onAction({ type: 'align', side: 'left' })}
          >
            <AlignLeft {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Align center"
            disabled={!enabled}
            onClick={() => onAction({ type: 'align', side: 'center' })}
          >
            <AlignCenter {...ICON} />
          </button>
          <button
            type="button"
            className="format-tool"
            title="Align right"
            disabled={!enabled}
            onClick={() => onAction({ type: 'align', side: 'right' })}
          >
            <AlignRight {...ICON} />
          </button>
        </div>

        <div className="format-separator" />

        <div className="format-group">
          <button
            type="button"
            className="format-tool"
            title="Insert link"
            disabled={!enabled}
            onClick={onInsertLink}
          >
            <Link {...ICON} />
          </button>
        </div>
      </div>
    </div>
  );
}
